import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";

type Theme = "light" | "dark";
const KEY = "mp-theme";

const Ctx = createContext<{ theme: Theme; setTheme: (t: Theme) => void; toggle: () => void } | null>(null);

function readInitial(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    const v = window.localStorage.getItem(KEY);
    if (v === "light" || v === "dark") return v;
  } catch {}
  return "dark";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const themedRoute = pathname.startsWith("/admin") || pathname.startsWith("/portal");

  // hydrate from localStorage on mount
  useEffect(() => { setThemeState(readInitial()); }, []);

  // apply class to <html>: themed routes follow preference, public site is always dark
  useEffect(() => {
    const root = document.documentElement;
    const effective: Theme = themedRoute ? theme : "dark";
    root.classList.toggle("dark", effective === "dark");
    root.classList.toggle("light", effective === "light");
    root.dataset.theme = effective;
  }, [theme, themedRoute]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    try { window.localStorage.setItem(KEY, t); } catch {}
  };
  const toggle = () => setTheme(theme === "dark" ? "light" : "dark");

  return <Ctx.Provider value={{ theme, setTheme, toggle }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useTheme must be used inside ThemeProvider");
  return c;
}